// AtlasForge Financial — Blog hero image mapping
//
// All 33 blog hero images are AI-generated (Gemini Nano Banana) and stored
// under /public/blog-images/<slug>.png, produced by
// /app/scripts/gen_blog_hero_images.py. Two visual families:
//
//   1. Personal Finance → photorealistic editorial photography of diverse
//      people using modern mobile banking apps in warm modern spaces.
//
//   2. Developer / Compliance / Industry / Strategy → editorial vector
//      illustrations in strict AtlasForge palette (navy #0A1128, coral
//      orange #FF5A36, off-white, charcoal).
//
// Any post without a mapping falls back to the emoji-on-gradient card so
// nothing crashes if a new post is added before an image is assigned.
// See the generator script's PROMPTS dict for the source-of-truth prompts.

const ALL_SLUGS = [
  // Personal Finance
  "stop-living-paycheck-to-paycheck-2026",
  "best-budgeting-apps-2026-comparison",
  "emergency-fund-how-much-2026",
  "what-is-safe-to-spend-personal-finance-explained",
  "50-30-20-budget-rule-vs-safe-to-spend",
  "best-high-yield-savings-accounts-2026",
  "buy-now-pay-later-klarna-affirm-afterpay-guide",
  "cash-back-credit-cards-vs-debit-2026",
  "credit-card-debt-payoff-snowball-vs-avalanche",
  "financial-independence-retire-early-fire-2026",
  "first-paycheck-guide-what-to-do-with-money-at-22",
  "how-to-avoid-overdraft-fees-forever",
  "how-to-save-1000-fast-30-day-plan",
  "how-to-talk-to-partner-about-money",
  "sinking-funds-explained-2026",
  // Developers
  "banking-apis-for-fintechs-2026-guide",
  "how-to-build-real-time-cash-flow-api",
  "webhook-signature-verification-fintech-security",
  // Compliance
  "gdpr-data-deletion-fintech-guide-2026",
  "pci-dss-4-compliance-fintech-founders-2026",
  "soc2-type-2-early-stage-fintech-playbook",
  "kyc-vendors-compared-alloy-persona-onfido-2026",
  // Industry
  "open-banking-vs-screen-scraping-2026",
  "embedded-finance-guide-non-fintech-companies",
  "1033-rule-explained-us-fintech-2027",
  "how-neobanks-make-money-2026",
  "card-issuing-lithic-vs-marqeta-vs-highnote",
  "plaid-vs-mx-vs-finicity-2026",
  "stripe-vs-adyen-vs-braintree-payment-processor-2026",
  // Strategy
  "ai-in-fintech-where-llms-actually-add-value-2026",
  "fintech-b2b-sales-first-10-customers",
  "fintech-fraud-prevention-2026",
  "fintech-stack-every-saas-tool-2026",
];

// Second wave — 30 SEO-optimized posts added Feb 23, 2026. Images shipped
// as WebP (avg ~55KB each vs 1.3MB PNG) so the blog index page stays fast.
const NEW_SLUGS_WEBP = [
  "high-yield-savings-account-strategy-2027",
  "no-spend-challenge-that-actually-works",
  "credit-utilization-under-10-percent",
  "emergency-fund-target-2027",
  "ai-financial-advisor-vs-human",
  "digital-envelope-budgeting-modernized",
  "bnpl-hidden-costs-2027",
  "subscription-audit-playbook",
  "dollar-cost-averaging-actually-work",
  "cash-flow-forecasting-consumer",
  "stripe-vs-plaid-vs-atlasforge",
  "webhook-idempotency-patterns",
  "api-key-rotation-without-downtime",
  "rate-limiting-strategies-fintech",
  "open-banking-uk-vs-us-2027",
  "gdpr-vs-ccpa-fintech-checklist",
  "aml-kyc-onboarding-flow-2027",
  "pci-dss-4-explained-plain-english",
  "real-time-payments-fednow-adoption",
  "stablecoin-in-consumer-fintech",
  "embedded-finance-vertical-saas",
  "bank-fintech-partnership-teardown",
  "cbdc-what-consumers-should-know",
  "financial-wellness-benefit-employers",
  "net-worth-tracker-vs-budget-app",
  "cash-vs-credit-rewards-optimization",
  "debt-avalanche-vs-snowball-2027",
  "small-business-cash-flow-tools",
  "tax-loss-harvesting-plain-english",
  "retirement-planning-freelancers",
];

const HERO_IMAGES = {
  "best-savings-account-rates-january-2027": "/blog-images/best-savings-account-rates-january-2027.png",
  "rent-vs-buy-calculator-real-numbers-2027": "/blog-images/rent-vs-buy-calculator-real-numbers-2027.png",
  "ai-and-your-personal-finances-2027": "/blog-images/ai-and-your-personal-finances-2027.png",
  "gig-worker-quarterly-taxes-guide-2027": "/blog-images/gig-worker-quarterly-taxes-guide-2027.png",
  "webhook-signing-hmac-vs-jwt-2027": "/blog-images/webhook-signing-hmac-vs-jwt-2027.png",
  "load-testing-payment-apis-2027": "/blog-images/load-testing-payment-apis-2027.png",
  "feature-flags-fintech-safe-deploys": "/blog-images/feature-flags-fintech-safe-deploys.png",
  "postgres-partitioning-financial-time-series": "/blog-images/postgres-partitioning-financial-time-series.png",
  "cfpb-1071-timelines-what-lenders-must-do-2027": "/blog-images/cfpb-1071-timelines-what-lenders-must-do-2027.png",
  "cfpb-1033-open-banking-final-rule-2027": "/blog-images/cfpb-1033-open-banking-final-rule-2027.png",
  "credit-card-competition-act-status-2027": "/blog-images/credit-card-competition-act-status-2027.png",
  "ai-in-fintech-nydfs-guidance-2027": "/blog-images/ai-in-fintech-nydfs-guidance-2027.png",
  "gen-z-banking-preferences-2027-data": "/blog-images/gen-z-banking-preferences-2027-data.png",
  "fednow-adoption-2-years-later-data": "/blog-images/fednow-adoption-2-years-later-data.png",
  "open-banking-us-vs-eu-2027-comparison": "/blog-images/open-banking-us-vs-eu-2027-comparison.png",
  "stablecoin-b2b-payments-adoption-2027": "/blog-images/stablecoin-b2b-payments-adoption-2027.png",
  "prenup-financial-planning-conversation": "/blog-images/prenup-financial-planning-conversation.png",
  "year-end-tax-moves-2026-deadline-checklist": "/blog-images/year-end-tax-moves-2026-deadline-checklist.png",
  "job-loss-financial-triage-first-90-days": "/blog-images/job-loss-financial-triage-first-90-days.png",
  "annual-financial-review-checklist-2027": "/blog-images/annual-financial-review-checklist-2027.png",
  "checking-account-fees-hidden-charges-2027": "/blog-images/checking-account-fees-hidden-charges-2027.png",
  "negotiate-medical-bills-2027": "/blog-images/negotiate-medical-bills-2027.png",
  "credit-score-boost-30-days-guide": "/blog-images/credit-score-boost-30-days-guide.png",
  "student-loan-payoff-fast-strategy-2027": "/blog-images/student-loan-payoff-fast-strategy-2027.png",
  "hsa-triple-tax-advantage-explained": "/blog-images/hsa-triple-tax-advantage-explained.png",
  "side-hustle-tax-guide-1099": "/blog-images/side-hustle-tax-guide-1099.png",
  "build-credit-from-zero-2027": "/blog-images/build-credit-from-zero-2027.png",
  "roth-ira-vs-traditional-ira-beginner-guide": "/blog-images/roth-ira-vs-traditional-ira-beginner-guide.png",
  "joint-vs-separate-accounts-couples": "/blog-images/joint-vs-separate-accounts-couples.png",
  "teen-first-bank-account-2027": "/blog-images/teen-first-bank-account-2027.png",
  "inflation-proof-savings-2027": "/blog-images/inflation-proof-savings-2027.png",
  "how-much-house-can-i-afford-2027": "/blog-images/how-much-house-can-i-afford-2027.png",
  "renters-insurance-worth-it-2027": "/blog-images/renters-insurance-worth-it-2027.png",
  "term-vs-whole-life-insurance-2027": "/blog-images/term-vs-whole-life-insurance-2027.png",
  "car-buying-cash-loan-lease-2027": "/blog-images/car-buying-cash-loan-lease-2027.png",
  "start-investing-with-100-dollars-2027": "/blog-images/start-investing-with-100-dollars-2027.png",
  "index-funds-vs-etfs-beginner-2027": "/blog-images/index-funds-vs-etfs-beginner-2027.png",
  "save-for-wedding-realistic-plan": "/blog-images/save-for-wedding-realistic-plan.png",
  "ledger-database-design-fintech": "/blog-images/ledger-database-design-fintech.png",
  "first-home-down-payment-2027": "/blog-images/first-home-down-payment-2027.png",
  "double-entry-bookkeeping-for-engineers": "/blog-images/double-entry-bookkeeping-for-engineers.png",
  "fintech-microservices-vs-monolith-2027": "/blog-images/fintech-microservices-vs-monolith-2027.png",
  "postgres-vs-mongo-financial-data": "/blog-images/postgres-vs-mongo-financial-data.png",
  "webhooks-that-scale-fintech-2027": "/blog-images/webhooks-that-scale-fintech-2027.png",
  "oauth-2-fintech-flows-explained": "/blog-images/oauth-2-fintech-flows-explained.png",
  "banking-api-error-codes-best-practices": "/blog-images/banking-api-error-codes-best-practices.png",
  "fintech-testing-strategy-2027": "/blog-images/fintech-testing-strategy-2027.png",
  "audit-log-design-fintech-2027": "/blog-images/audit-log-design-fintech-2027.png",
  "fraud-detection-rules-vs-ml-2027": "/blog-images/fraud-detection-rules-vs-ml-2027.png",
  "transaction-categorization-ml-2027": "/blog-images/transaction-categorization-ml-2027.png",
  "plaid-webhook-integration-tutorial": "/blog-images/plaid-webhook-integration-tutorial.png",
  "currency-conversion-api-design": "/blog-images/currency-conversion-api-design.png",
  "fintech-observability-datadog-honeycomb": "/blog-images/fintech-observability-datadog-honeycomb.png",
  "stripe-connect-marketplace-implementation": "/blog-images/stripe-connect-marketplace-implementation.png",
  "graphql-vs-rest-banking-apis": "/blog-images/graphql-vs-rest-banking-apis.png",
  "secrets-management-fintech-2027": "/blog-images/secrets-management-fintech-2027.png",
  "background-jobs-fintech-sidekiq-temporal": "/blog-images/background-jobs-fintech-sidekiq-temporal.png",
  "payment-reconciliation-system-design": "/blog-images/payment-reconciliation-system-design.png",
  "real-time-balance-websockets-sse": "/blog-images/real-time-balance-websockets-sse.png",
  "bsa-aml-startup-checklist-2027": "/blog-images/bsa-aml-startup-checklist-2027.png",
  "suspicious-activity-report-sar-guide": "/blog-images/suspicious-activity-report-sar-guide.png",
  "ofac-sanctions-screening-2027": "/blog-images/ofac-sanctions-screening-2027.png",
  "ctr-currency-transaction-report-2027": "/blog-images/ctr-currency-transaction-report-2027.png",
  "money-transmitter-license-guide-2027": "/blog-images/money-transmitter-license-guide-2027.png",
  "glba-compliance-fintech-2027": "/blog-images/glba-compliance-fintech-2027.png",
  "reg-e-electronic-funds-transfer-guide": "/blog-images/reg-e-electronic-funds-transfer-guide.png",
  "reg-cc-check-hold-rules-2027": "/blog-images/reg-cc-check-hold-rules-2027.png",
  "reg-z-truth-in-lending-fintech-2027": "/blog-images/reg-z-truth-in-lending-fintech-2027.png",
  "reg-ii-durbin-interchange-2027": "/blog-images/reg-ii-durbin-interchange-2027.png",
  "iso-27001-vs-soc-2-fintech": "/blog-images/iso-27001-vs-soc-2-fintech.png",
  "fdic-passthrough-insurance-fintech": "/blog-images/fdic-passthrough-insurance-fintech.png",
  "hipaa-fintech-healthtech-crossover": "/blog-images/hipaa-fintech-healthtech-crossover.png",
  "finra-crypto-broker-dealer-2027": "/blog-images/finra-crypto-broker-dealer-2027.png",
  "sec-crypto-custody-rule-2027": "/blog-images/sec-crypto-custody-rule-2027.png",
  "fair-lending-ecoa-fintech-2027": "/blog-images/fair-lending-ecoa-fintech-2027.png",
  "fcra-credit-reporting-compliance-2027": "/blog-images/fcra-credit-reporting-compliance-2027.png",
  "tila-respa-mortgage-fintech-2027": "/blog-images/tila-respa-mortgage-fintech-2027.png",
  "cfpb-1071-small-business-data-2027": "/blog-images/cfpb-1071-small-business-data-2027.png",
  "eu-dora-digital-resilience-2027": "/blog-images/eu-dora-digital-resilience-2027.png",
  "neobank-consolidation-2027-outlook": "/blog-images/neobank-consolidation-2027-outlook.png",
  "private-credit-fintech-boom": "/blog-images/private-credit-fintech-boom.png",
  "crypto-etf-consumer-impact-2027": "/blog-images/crypto-etf-consumer-impact-2027.png",
  "bnpl-regulation-crackdown-2027": "/blog-images/bnpl-regulation-crackdown-2027.png",
  "banking-as-a-service-vendor-map-2027": "/blog-images/banking-as-a-service-vendor-map-2027.png",
  "embedded-finance-market-size-2027": "/blog-images/embedded-finance-market-size-2027.png",
  "bank-charter-vs-bin-sponsorship-2027": "/blog-images/bank-charter-vs-bin-sponsorship-2027.png",
  "remittance-market-shift-2027": "/blog-images/remittance-market-shift-2027.png",
  "instant-payments-vs-cards-checkout-2027": "/blog-images/instant-payments-vs-cards-checkout-2027.png",
  "wealthtech-robo-advisor-consolidation-2027": "/blog-images/wealthtech-robo-advisor-consolidation-2027.png",
  "sme-lending-post-svb-era": "/blog-images/sme-lending-post-svb-era.png",
  "credit-card-rewards-economics-2027": "/blog-images/credit-card-rewards-economics-2027.png",
  "fintech-ipo-window-2027": "/blog-images/fintech-ipo-window-2027.png",
  "vertical-neobanks-rise-2027": "/blog-images/vertical-neobanks-rise-2027.png",
  "cross-border-b2b-payments-2027": "/blog-images/cross-border-b2b-payments-2027.png",
  "fintech-m-and-a-2026-summary": "/blog-images/fintech-m-and-a-2026-summary.png",
  "earned-wage-access-market-2027": "/blog-images/earned-wage-access-market-2027.png",
  "innovation-labs-worth-it-2027": "/blog-images/innovation-labs-worth-it-2027.png",
  "digital-lending-fed-rate-cuts-2027": "/blog-images/digital-lending-fed-rate-cuts-2027.png",
  "atm-networks-shrinking-2027": "/blog-images/atm-networks-shrinking-2027.png",
  "reverse-budget-savings-first": "/blog-images/reverse-budget-savings-first.png",
  "couples-money-conversation-framework": "/blog-images/couples-money-conversation-framework.png",
  "financial-goals-setting-2027": "/blog-images/financial-goals-setting-2027.png",
  "anti-budget-method-cashflow-first": "/blog-images/anti-budget-method-cashflow-first.png",
  "mega-backdoor-roth-2027-guide": "/blog-images/mega-backdoor-roth-2027-guide.png",
  "money-scripts-your-relationship-with-money": "/blog-images/money-scripts-your-relationship-with-money.png",
  "lifestyle-inflation-how-to-avoid": "/blog-images/lifestyle-inflation-how-to-avoid.png",
  "401k-employer-match-optimization-2027": "/blog-images/401k-employer-match-optimization-2027.png",
  "financial-independence-number-calculator": "/blog-images/financial-independence-number-calculator.png",
  "self-employed-retirement-2027": "/blog-images/self-employed-retirement-2027.png",
  "sabbatical-financial-planning-2027": "/blog-images/sabbatical-financial-planning-2027.png",
  "coast-fire-vs-lean-fire-vs-fat-fire": "/blog-images/coast-fire-vs-lean-fire-vs-fat-fire.png",
  "windfall-inheritance-what-to-do": "/blog-images/windfall-inheritance-what-to-do.png",
  "divorce-financial-checklist-2027": "/blog-images/divorce-financial-checklist-2027.png",
  "career-change-money-plan-2027": "/blog-images/career-change-money-plan-2027.png",
  "new-parent-financial-checklist-2027": "/blog-images/new-parent-financial-checklist-2027.png",
  "college-savings-529-vs-ugma-2027": "/blog-images/college-savings-529-vs-ugma-2027.png",
  "senior-parents-caregiving-financial": "/blog-images/senior-parents-caregiving-financial.png",
  "estate-planning-basics-under-40": "/blog-images/estate-planning-basics-under-40.png",
  "paying-for-college-without-loans-2027": "/blog-images/paying-for-college-without-loans-2027.png",
  "best-money-market-accounts-2027": "/blog-images/best-money-market-accounts-2027.png",
  ...Object.fromEntries(ALL_SLUGS.map((slug) => [slug, `/blog-images/${slug}.png`])),
  ...Object.fromEntries(NEW_SLUGS_WEBP.map((slug) => [slug, `/blog-images/${slug}.webp`])),
};

/** Returns the hero image URL for a blog post slug, or `null` if unmapped. */
export function getHeroImage(slug) {
  return HERO_IMAGES[slug] || null;
}

/** Absolute URL variant — safe for Open Graph / Twitter card social share tags. */
export function getHeroImageAbsolute(slug) {
  const url = HERO_IMAGES[slug];
  if (!url) return null;
  return url.startsWith("http") ? url : `https://atlasforgefinancial.com${url}`;
}

export default HERO_IMAGES;
